import { useCallback, useEffect, useRef } from 'react';
import { debounce } from '@mui/material';
import { toast } from 'react-toastify';
import { useEPMContext } from '../../../contexts/EPMContext';
import { useUserContext } from '../../../contexts/UserContext';
import { permission } from '../../../config/permission';

// How long typing has to pause before a text field is saved
export const AUTOSAVE_DELAY = 800;

// One toast id, so a run of saves shows one message instead of a stack
export const notifyAutoSaved = (message = 'Changes saved') =>
    toast.success(message, { toastId: 'epm-autosave', autoClose: 1500 });

/**
 * Auto-save for workflow fields. The server applies nested objects
 * (ideation, plans, triaged, verdict, ...) as partial updates, so each field
 * can be saved on its own.
 *
 * - saveNow(payload, key?): saves straight away (clicks, dropdowns, toggles).
 *   Passing the key of a pending debounced save drops it, as the payload replaces it.
 * - saveLater(key, pick): saves after typing pauses (text fields). `pick` builds
 *   the payload from the latest test when the save fires, so it always sends what
 *   is on screen. Each key has its own timer, so typing in one field never drops
 *   another field's save.
 * - flush(key?): sends a pending debounced save now (e.g. on blur).
 *
 * Anything still pending is sent when the step unmounts.
 */
const useAutoSave = (testData) => {
    const { updateTest } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const testId = testData?._id || testData?.id;

    const latest = useRef({ testData, testId, updateTest });
    latest.current = { testData, testId, updateTest };

    // key -> { run: debounced send, pick }
    const pending = useRef(new Map());

    const send = useCallback(async (payload) => {
        if (!canUpdate) return;
        const { testId: id, updateTest: update } = latest.current;
        if (!id || !update || !payload) return;
        try {
            await update(id, payload);
            notifyAutoSaved();
        } catch (err) {
            console.error('Auto-save failed:', err);
            toast.error(err?.message || "Couldn't save your changes");
        }
    }, [canUpdate]);

    const sendPending = useCallback((key) => {
        if (!canUpdate) return;
        const entry = pending.current.get(key);
        if (!entry) return;
        entry.run.clear();
        pending.current.delete(key);

        // If a format validator is supplied, abort send if format validation fails
        if (typeof entry.validator === 'function') {
            const validationError = entry.validator(latest.current.testData);
            if (validationError) return;
        }

        send(entry.pick(latest.current.testData));
    }, [canUpdate, send]);

    const saveLater = useCallback((key, pick, validator) => {
        if (!canUpdate) return;
        let entry = pending.current.get(key);
        if (!entry) {
            entry = { run: debounce(() => sendPending(key), AUTOSAVE_DELAY), pick, validator };
            pending.current.set(key, entry);
        }
        entry.pick = pick;
        entry.validator = validator;
        entry.run();
    }, [canUpdate, sendPending]);

    const saveNow = useCallback((payload, key, validator) => {
        if (!canUpdate) return Promise.resolve();
        if (typeof validator === 'function') {
            const validationError = validator(payload);
            if (validationError) return Promise.resolve();
        }
        if (key && pending.current.has(key)) {
            pending.current.get(key).run.clear();
            pending.current.delete(key);
        }
        return send(payload);
    }, [canUpdate, send]);

    const flush = useCallback((key) => {
        if (!canUpdate) return;
        const keys = key ? [key] : [...pending.current.keys()];
        keys.forEach(sendPending);
    }, [canUpdate, sendPending]);

    useEffect(() => () => flush(), [flush]);

    return { saveNow, saveLater, flush };
};

export default useAutoSave;
